import { Tooltip } from '@fluentui/react-northstar';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getNodesByType, NodeType } from '../../../shared/cache/NodeCache';
import { STATUS_GUID } from '../../../shared/utils/constants';
import { getStatusIdByGuid } from '../../ticketHelper';
import { getActiveMenuKey, isCustomFormTabLocation, setActiveMenuKey } from '../storageKeys';

export interface MenuItem {
  key: string;
  content: string;
  tooltip: string;
  nodeId: number | null;
  children: (Component: any, props: any) => React.ReactElement;
}

interface UseTicketMenuItemsArgs {
  ticket: any;
  ticketStatus: any[] | undefined;
  isLifecycleEnabled: boolean;
  isResolutionNoteEnabled: boolean;
  isResolutionNoteEnabledByConfig: boolean;
  /**
   * Both the Ticket Configuration toggle and the caller's role, resolved together by
   * EditTicket. Agents, Super Agents and Admins only.
   */
  isTimeTrackingVisible: boolean;
  lifecycleMenuOpen: boolean;
  lifecyclePhaseMenu: number | undefined;
  setAppState: (updater: any) => void;
  onReloadPhases: () => void | Promise<void>;
  t: TFunction;
}

// Module-scoped cache so the custom-tab fetch is performed once per app session,
// rather than re-running every time the ticket id / status / lifecycle changes.
let customNodeTabsPromise: Promise<any[]> | null = null;
const fetchCustomNodeTabsOnce = (): Promise<any[]> => {
  if (!customNodeTabsPromise) {
    customNodeTabsPromise = getNodesByType(NodeType.Tab).catch(error => {
      console.error('Error fetching custom tab nodes:', error);
      customNodeTabsPromise = null;
      return [];
    });
  }
  return customNodeTabsPromise;
};

/**
 * Owns the EditTicket center-pane tab menu (activity / customForm / lifecycle /
 * resolution / node-*), the active tab index, and the localStorage-restored /
 * lifecycle-driven auto-selection logic.
 */
export const useTicketMenuItems = ({
  ticket,
  ticketStatus,
  isLifecycleEnabled,
  isResolutionNoteEnabled,
  isResolutionNoteEnabledByConfig,
  isTimeTrackingVisible,
  lifecycleMenuOpen,
  lifecyclePhaseMenu,
  setAppState,
  onReloadPhases,
  t
}: UseTicketMenuItemsArgs) => {
  const [menu, setMenu] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [oldStatusId, setOldStatusId] = useState<any>(null);
  const [customNodeTabs, setCustomNodeTabs] = useState<MenuItem[] | null>(null);

  const customFormTabLocation = useMemo(() => isCustomFormTabLocation(), []);

  const itemRenderer = useCallback(
    (MenuItem: any, props: any) => {
      const { tooltip = '', key, ...rest } = props;
      if (key === 'template' && (ticket.TemplateId || 0) <= 0) rest.disabled = true;
      return (
        <Tooltip content={tooltip} key={key}>
          <MenuItem {...rest} />
        </Tooltip>
      );
    },
    [ticket.TemplateId]
  );

  // Fetch custom node tabs exactly once (module-cached).
  useEffect(() => {
    let cancelled = false;
    fetchCustomNodeTabsOnce().then(nodes => {
      if (cancelled) return;
      const mapped: MenuItem[] = (nodes || []).map(n => ({
        key: `node-${n.Id}`,
        content: n.Name,
        tooltip: n.Name,
        nodeId: n.Id,
        children: itemRenderer
      }));
      setCustomNodeTabs(mapped);
    });
    return () => {
      cancelled = true;
    };
  }, [itemRenderer]);

  // Build the synchronous tab list (activity / customForm / lifecycle / resolution).
  const baseMenuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        key: 'activity',
        content: t('ticket-details.menu.activity'),
        tooltip: t('ticket-details.menu.tooltip'),
        nodeId: null,
        children: itemRenderer
      }
    ];

    if (ticket.CustomForms?.length > 0 && customFormTabLocation) {
      items.push({
        key: 'customForm',
        content: t('ticket-details.edit-ticket-form.custom-form-answer.title'),
        tooltip: t('ticket-details.edit-ticket-form.custom-form-answer.tooltip'),
        nodeId: null,
        children: itemRenderer
      });
    }

    if (ticket.TicketLifecycleId != undefined && isLifecycleEnabled) {
      items.push({
        key: 'lifecycle',
        content: t('common.lifecycle-with-space'),
        tooltip: t('common.show-lifecycle'),
        nodeId: null,
        children: itemRenderer
      });
    }

    if (isResolutionNoteEnabled && isResolutionNoteEnabledByConfig) {
      const resolvedStatusId = getStatusIdByGuid(ticketStatus ?? [], STATUS_GUID.RESOLVED);
      const closedStatusId = getStatusIdByGuid(ticketStatus ?? [], STATUS_GUID.CLOSED);
      const isResolveOrClosed = [resolvedStatusId, closedStatusId].includes(ticket.StatusId);
      if (isResolveOrClosed || ticket?.ResolutionId != null) {
        items.push({
          key: 'resolution',
          content: t('resolution-note.details-tab'),
          tooltip: t('resolution-note.details-tab'),
          nodeId: null,
          children: itemRenderer
        });
      }
    }

    // Must stay last. Custom node tabs are appended after baseMenuItems below, so
    // pushing here puts Time Tracking last among the built-in tabs and still ahead of
    // every customer-added tab.
    if (isTimeTrackingVisible) {
      items.push({
        key: 'timeTracking',
        content: t('time-tracking.tab-title'),
        tooltip: t('time-tracking.tab-tooltip'),
        nodeId: null,
        children: itemRenderer
      });
    }

    return items;
  }, [
    isTimeTrackingVisible,
    ticket.CustomForms,
    ticket.TicketLifecycleId,
    ticket.StatusId,
    ticket.ResolutionId,
    ticketStatus,
    isLifecycleEnabled,
    isResolutionNoteEnabled,
    isResolutionNoteEnabledByConfig,
    customFormTabLocation,
    itemRenderer,
    t
  ]);

  // Combine base + custom node tabs.
  useEffect(() => {
    const combined = customNodeTabs && customNodeTabs.length > 0
      ? [...baseMenuItems, ...customNodeTabs]
      : baseMenuItems;
    setMenuItems(combined);
  }, [baseMenuItems, customNodeTabs]);

  // Auto-select the resolution tab when status transitions into Resolved/Closed.
  useEffect(() => {
    if (!ticket.StatusId) return;
    const statusChanged = oldStatusId != ticket.StatusId;
    if (!statusChanged) return;

    if (oldStatusId != null && isResolutionNoteEnabled && isResolutionNoteEnabledByConfig) {
      const resolvedStatusId = getStatusIdByGuid(ticketStatus ?? [], STATUS_GUID.RESOLVED);
      const closedStatusId = getStatusIdByGuid(ticketStatus ?? [], STATUS_GUID.CLOSED);
      const isResolveOrClosed = [resolvedStatusId, closedStatusId].includes(ticket.StatusId);
      if (isResolveOrClosed) {
        const resolutionIndex = baseMenuItems.findIndex(x => x.key === 'resolution');
        // Legacy guard, carried over as `_menuItems.length != 3`. Its comment used to
        // claim it fires only above 3 base tabs, which was never true - with resolution
        // present the reachable counts are 2, 3 and 4, so it fires at 2 as well.
        //
        // Time Tracking is excluded from the count deliberately. Including it would
        // shift those counts to 3, 4 and 5 and invert the behaviour for exactly the
        // tenants that enable the feature. Whether the guard itself is right is a
        // separate question; this keeps it behaving identically either way.
        const legacyBaseCount = baseMenuItems.filter(x => x.key !== 'timeTracking').length;
        if (resolutionIndex >= 0 && legacyBaseCount !== 3) {
          setMenu(resolutionIndex);
        }
      }
    }

    setOldStatusId(ticket.StatusId);
  }, [
    ticket.StatusId,
    oldStatusId,
    isResolutionNoteEnabled,
    isResolutionNoteEnabledByConfig,
    ticketStatus,
    baseMenuItems
  ]);

  // Sync active menu index with lifecycle / saved localStorage value.
  useEffect(() => {
    if (menuItems.length === 0) return;
    const lifecycleMenuIndex = menuItems.findIndex(x => x.key === 'lifecycle');
    if (isLifecycleEnabled && lifecycleMenuOpen && lifecycleMenuIndex >= 0) {
      const currentPhaseOrder =
        ticket['TicketLifecycle']?.['Phases']?.find((o: any) => o.IsCurrent)?.Order ?? 0;
      const phaseOrder = lifecyclePhaseMenu ? lifecyclePhaseMenu : currentPhaseOrder;
      setAppState((prev: any) => ({ ...prev, lifecyclePhaseMenu: phaseOrder }));
      if (menu !== lifecycleMenuIndex) {
        setMenu(lifecycleMenuIndex);
        setActiveMenuKey(menuItems[lifecycleMenuIndex].key);
      }
    } else {
      const savedKey = getActiveMenuKey();
      const idx = menuItems.findIndex(x => x.key === savedKey);
      setMenu(idx >= 0 ? idx : 0);
      if (savedKey === 'lifecycle' && idx >= 0 && isLifecycleEnabled) {
        setAppState((prev: any) => ({ ...prev, lifecycleMenuOpen: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, lifecycleMenuOpen, isLifecycleEnabled]);

  const menuIndexChange = useCallback(
    (_e: any, props: any) => {
      const nextIndex: number = props.activeIndex;
      setMenu(nextIndex);
      setActiveMenuKey(menuItems[nextIndex].key);

      const lifecycleMenuIndex = menuItems.findIndex(x => x.key === 'lifecycle');
      if (nextIndex !== lifecycleMenuIndex) {
        setAppState((prev: any) => ({
          ...prev,
          lifecycleMenuOpen: false,
          lifecyclePhaseMenu: undefined
        }));
      } else {
        setAppState((prev: any) => ({ ...prev, lifecycleMenuOpen: true }));
        onReloadPhases();
      }
    },
    [menuItems, setAppState, onReloadPhases]
  );

  return { menu, setMenu, menuItems, menuIndexChange };
};
